import React, { useEffect, useState } from "react";
import { Send, Trash2, Mail, Users, History, UserPlus, Upload, X } from "lucide-react";
import {
  adminListSubscribers,
  adminDeleteSubscriber,
  adminSendBroadcast,
  adminBroadcastHistory,
} from "../../lib/api";
import { api } from "../../lib/api";
import { toast } from "sonner";

const NewsletterAdminPage = () => {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [history, setHistory] = useState([]);
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingMode, setAddingMode] = useState(null); // null | "single" | "bulk"
  const [singleEmail, setSingleEmail] = useState("");
  const [singleName, setSingleName] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [addingBusy, setAddingBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [subsRes, histRes] = await Promise.all([
        adminListSubscribers(),
        adminBroadcastHistory(),
      ]);
      setSubs(subsRes.items || []);
      setStats({ total: subsRes.total || 0, active: subsRes.active || 0 });
      setHistory(histRes.items || []);
    } catch {
      toast.error("Konnte Newsletter-Daten nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Subscriber wirklich löschen?")) return;
    try {
      await adminDeleteSubscriber(id);
      toast.success("Gelöscht");
      await load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const send = async () => {
    if (!subject.trim() || !html.trim()) {
      toast.error("Betreff und Inhalt sind Pflicht");
      return;
    }
    if (!window.confirm(`Newsletter an ${stats.active} aktive Empfänger senden?`)) return;
    setSending(true);
    try {
      const res = await adminSendBroadcast({ subject, html, preheader });
      toast.success(`Broadcast gequeued für ${res.queued_for} Empfänger`);
      setSubject("");
      setPreheader("");
      setHtml("");
      setTimeout(load, 800);
    } catch {
      toast.error("Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="text-white">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-pink-400">
          <Mail size={12} /> Backoffice
        </div>
        <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl tracking-tight">
          Newsletter
        </h1>
        <p className="mt-2 text-sm text-neutral-400">
          Subscriber verwalten und Broadcasts versenden. Neue Leads werden automatisch abonniert.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="p-5 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Aktiv</div>
          <div className="mt-1 font-display font-extrabold text-3xl">{stats.active}</div>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Gesamt</div>
          <div className="mt-1 font-display font-extrabold text-3xl">{stats.total}</div>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Broadcasts</div>
          <div className="mt-1 font-display font-extrabold text-3xl">{history.length}</div>
        </div>
      </div>

      {/* Add subscriber */}
      <div className="mb-8 p-5 rounded-2xl bg-white/[0.04] border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold flex items-center gap-2">
            <UserPlus size={16} className="text-pink-400" /> Subscriber hinzufügen
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => setAddingMode(addingMode === "single" ? null : "single")}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${addingMode === "single" ? "bg-pink-500 text-white" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
              data-testid="newsletter-add-single-toggle"
            >
              Einzeln
            </button>
            <button
              onClick={() => setAddingMode(addingMode === "bulk" ? null : "bulk")}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${addingMode === "bulk" ? "bg-pink-500 text-white" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
              data-testid="newsletter-add-bulk-toggle"
            >
              Mehrere (Bulk)
            </button>
          </div>
        </div>

        {addingMode === "single" && (
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
            <input
              type="email"
              placeholder="empfaenger@mail.de"
              value={singleEmail}
              onChange={(e) => setSingleEmail(e.target.value)}
              className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
              data-testid="newsletter-single-email"
            />
            <input
              type="text"
              placeholder="Name (optional)"
              value={singleName}
              onChange={(e) => setSingleName(e.target.value)}
              className="px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
            />
            <button
              onClick={async () => {
                if (!/^\S+@\S+\.\S+$/.test(singleEmail)) return toast.error("Ungültige E-Mail");
                setAddingBusy(true);
                try {
                  const r = await api.post("/admin/newsletter/subscribers", { email: singleEmail, name: singleName, source: "admin" });
                  toast.success(r.data.created ? "Hinzugefügt" : "Existiert bereits (reaktiviert falls inaktiv)");
                  setSingleEmail(""); setSingleName("");
                  await load();
                } catch (e) {
                  toast.error("Fehler: " + (e?.response?.data?.detail || e.message));
                } finally {
                  setAddingBusy(false);
                }
              }}
              disabled={addingBusy}
              className="px-5 py-3 rounded-xl bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-xs font-bold uppercase tracking-widest disabled:opacity-60"
              data-testid="newsletter-single-submit"
            >
              Hinzufügen
            </button>
          </div>
        )}

        {addingMode === "bulk" && (
          <div>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="E-Mail-Adressen (getrennt durch Komma, Zeilenumbruch oder Semikolon)"
              rows={5}
              className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm font-mono"
              data-testid="newsletter-bulk-text"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-xs text-neutral-500">
                Ungültige Adressen werden übersprungen. Duplikate werden reaktiviert.
              </div>
              <button
                onClick={async () => {
                  if (!bulkText.trim()) return toast.error("Bitte E-Mails eingeben");
                  setAddingBusy(true);
                  try {
                    const r = await api.post("/admin/newsletter/subscribers/bulk", { emails: bulkText });
                    toast.success(`+${r.data.created} neu · ${r.data.skipped} übersprungen`);
                    setBulkText("");
                    await load();
                  } catch (e) {
                    toast.error("Fehler: " + (e?.response?.data?.detail || e.message));
                  } finally {
                    setAddingBusy(false);
                  }
                }}
                disabled={addingBusy}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-xs font-bold uppercase tracking-widest disabled:opacity-60"
                data-testid="newsletter-bulk-submit"
              >
                <Upload size={13} /> Importieren
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Compose */}
      <div className="mb-10 p-6 rounded-2xl bg-white/[0.04] border border-white/10 space-y-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Send size={16} className="text-pink-400" />
          Broadcast senden
        </h2>
        <input
          placeholder='Betreff (z.B. „Was diesen Monat funktioniert hat")'
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
          data-testid="newsletter-subject-input"
        />
        <input
          placeholder="Preheader (optional – kurzer Preview-Text)"
          value={preheader}
          onChange={(e) => setPreheader(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm"
        />
        <textarea
          placeholder="HTML-Inhalt der Mail (kann HTML enthalten, z.B. <p>…</p>, <a>…</a>, <b>…</b>)"
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          rows={10}
          className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 outline-none focus:border-pink-400/60 text-sm font-mono"
          data-testid="newsletter-html-input"
        />
        <div className="flex items-center justify-between gap-4">
          <div className="text-xs text-neutral-400">
            Empfänger: <b className="text-white">{stats.active}</b> aktive Subscriber
          </div>
          <button
            onClick={send}
            disabled={sending || stats.active === 0}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-xs font-bold uppercase tracking-[0.12em] disabled:opacity-60"
            data-testid="newsletter-send-button"
          >
            <Send size={14} /> {sending ? "Senden…" : "Broadcast senden"}
          </button>
        </div>
      </div>

      {/* Subscribers list */}
      <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
        <Users size={16} className="text-pink-400" /> Subscriber
      </h2>
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden mb-10">
        {loading ? (
          <div className="p-6 text-neutral-400 text-sm">Laden…</div>
        ) : subs.length === 0 ? (
          <div className="p-6 text-neutral-400 text-sm">Noch keine Subscriber.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-neutral-400 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">E-Mail</th>
                <th className="text-left px-5 py-3">Quelle</th>
                <th className="text-left px-5 py-3">Erstellt</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-white/5"
                  data-testid={`subscriber-row-${s.id}`}
                >
                  <td className="px-5 py-3 text-neutral-100">{s.email}</td>
                  <td className="px-5 py-3 text-neutral-400 text-xs uppercase tracking-widest">
                    {s.source}
                  </td>
                  <td className="px-5 py-3 text-neutral-400 text-xs">
                    {new Date(s.created_at).toLocaleDateString("de-DE")}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-widest font-bold ${
                        s.is_active ? "text-emerald-400" : "text-neutral-500"
                      }`}
                    >
                      {s.is_active ? "Aktiv" : "Inaktiv"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => remove(s.id)}
                      className="p-2 rounded-full border border-white/10 text-red-400 hover:bg-red-500/10"
                      title="Löschen"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <>
          <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
            <History size={16} className="text-pink-400" /> Broadcast-Historie
          </h2>
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-neutral-400 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-5 py-3">Datum</th>
                  <th className="text-left px-5 py-3">Betreff</th>
                  <th className="text-left px-5 py-3">Empfänger</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-white/5">
                    <td className="px-5 py-3 text-neutral-400 text-xs">
                      {new Date(h.created_at).toLocaleString("de-DE")}
                    </td>
                    <td className="px-5 py-3 text-neutral-100">{h.subject}</td>
                    <td className="px-5 py-3 text-neutral-400">
                      {h.sent_ok || 0}/{h.recipients_count || 0}
                      {h.sent_fail > 0 && (
                        <span className="text-red-400 ml-2">
                          ({h.sent_fail} failed)
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[10px] uppercase tracking-widest font-bold text-neutral-300">
                      {h.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default NewsletterAdminPage;
